import { LineString, Point, Polygon, Position } from '@turf/turf';
import { exportEntity, searchEntities } from 'api/entities';
import { AccessRuleCode, mapEntityParams } from 'constants/entities';
import { errorMessages, infoMessages } from 'constants/errors';
import { HIERARCHY_MAX_DEPTH, ROOT_HIERARCHY_ID } from 'constants/hierarchy';
import { FeatureTypes } from 'constants/map';
import {
  EntityWithRelationOption,
  IMediaFile,
  ISelectOption,
} from 'interfaces';
import { Entities } from 'store/slices/mapV2/tabsReducer/layersReducer/mapEntitiesSlice/types';
import { CameraBounds } from 'types';

import { extractIcons } from 'components/Map/Tabs/QuickEntities/utils';
import { downloadObject, notify } from 'utils';

import {
  AccessRules,
  Entity,
  EntityParameter,
  EntityWithRelations,
  GeometryParameter,
  ParametersIdTitleMap,
  PredefinedTemplate,
} from '../types/entities';

const VIEW_BOX_REGEX = new RegExp('viewBox=".*?"');

export interface PossibleValuesMap {
  [key: number]: string[];
}

export const createEntityWithRelations = (
  entity: Entity,
  accessRules: AccessRules,
  parentEntityID?: number
): EntityWithRelations => ({
  entity: entity,
  childIDs: [],
  parentIDs: parentEntityID ? [parentEntityID] : [ROOT_HIERARCHY_ID],
  ...accessRules,
});

export const getEntityPath = (
  entities: EntityWithRelations[],
  entityId: number
): number[] => {
  const entity = entities.find((entity) => entity.entity.id === entityId);
  const parentId = entity?.parentIDs?.[0];

  if (!entity || !parentId) {
    return [ROOT_HIERARCHY_ID];
  }

  return [...getEntityPath(entities, parentId), parentId];
};

export const getEntityFullPath = (
  entities: EntityWithRelations[],
  entityId: number
) => [...getEntityPath(entities, entityId), entityId];

export const extractParamPossibleValues = (
  entityRelatedParameters: Record<number, EntityParameter[]>
): PossibleValuesMap => {
  const possibleValuesMap: PossibleValuesMap = {};

  for (const key in entityRelatedParameters) {
    const paramsArray = entityRelatedParameters[key];

    if (paramsArray) {
      paramsArray.forEach((param) => {
        possibleValuesMap[param.id] = (param.settings.allowedValues || []).map(
          (value) => String(value)
        );
      });
    }
  }
  return possibleValuesMap;
};

export const getEntityParameterIdByTitle = (
  parameters: EntityParameter[],
  title: string
) => String(parameters.find((parameter) => parameter.title === title)?.id ?? 0);

export const getEntityParametersIdToTitleMap = (
  parameters: EntityParameter[],
  parameterTitles: string[]
) =>
  parameterTitles.reduce((acc, curr) => {
    const id = getEntityParameterIdByTitle(parameters, curr);

    acc[id] = curr;

    return acc;
  }, {} as Record<string, string>);

export const getEntityParametersTitleToIdMap = (
  parameters: EntityParameter[],
  parameterTitles: string[]
) =>
  parameterTitles.reduce((acc, curr) => {
    const id = getEntityParameterIdByTitle(parameters, curr);

    acc[curr] = id;

    return acc;
  }, {} as Record<string, string>);

export const getEntityParameterValue = <T>(
  entity: Entity | null | undefined,
  parametersIdTitleMap: Record<string, string>,
  parameterTitle: string
): T | null =>
  parametersIdTitleMap
    ? entity?.parameters[parametersIdTitleMap[parameterTitle]]?.value
    : null;

export const createEmptyEntity = (
  templateId: number,
  parentId?: number
): Entity => ({
  id: 0,
  templateID: templateId,
  title: '',
  parentEntityID: parentId,
  createdBy: {},
  parameters: {},
});

export const convertEntityWithRelationsToSelectOptions = (
  mapLayerTemplateId: number,
  entities: EntityWithRelations[],
  excludeIDs?: number[],
  filterByReadwrite?: boolean
): EntityWithRelationOption[] => {
  const entityMap = new Map<number, EntityWithRelations>();

  entities.forEach((entityWithRelations) => {
    entityMap.set(entityWithRelations.entity.id, entityWithRelations);
  });

  const isIdExcluded = (id: number) => excludeIDs && excludeIDs.includes(id);

  function buildOption(
    entityWithRelations: EntityWithRelations
  ): EntityWithRelationOption {
    const { entity, childIDs, upmostParentID, isPersonalSpace } =
      entityWithRelations;

    const option: EntityWithRelationOption = {
      id: entity.id,
      label: entity.title,
      value: entity.id,
      children: null,
      properties: { upmostParentId: upmostParentID },
      isPersonalSpace,
    };

    if (childIDs.length > 0) {
      const childrenOptions = childIDs.flatMap((childID) => {
        const childEntityWithRelations = entityMap.get(childID);

        if (
          isIdExcluded(childID) ||
          childEntityWithRelations?.entity?.templateID !== mapLayerTemplateId
        ) {
          return [];
        }

        return [buildOption(childEntityWithRelations)];
      });

      if (childrenOptions.length > 0) {
        option.children = childrenOptions;
      }
    }

    return option;
  }

  const rootOptions: EntityWithRelationOption[] = entities.flatMap((entity) => {
    if (
      isIdExcluded(entity.entity.id) ||
      entity.parentIDs.length !== 0 ||
      (filterByReadwrite && entity.accessType !== AccessRuleCode.READWRITE)
    ) {
      return [];
    }

    return buildOption(entity);
  });

  return rootOptions;
};

export const getMapLayerOptions = async (
  mapLayerTemplateId: number,
  settings: {
    selectOptionsExcludeIDs?: number[];
    recentProjects?: number[];
    filterByReadwrite?: boolean;
  }
) => {
  const { selectOptionsExcludeIDs, recentProjects, filterByReadwrite } =
    settings;

  const { entities } = await searchEntities({
    maxDepth: HIERARCHY_MAX_DEPTH,
    parentEntityID: 0,
    templateIDs: [mapLayerTemplateId],
  });

  const options = convertEntityWithRelationsToSelectOptions(
    mapLayerTemplateId,
    entities,
    selectOptionsExcludeIDs,
    filterByReadwrite
  );

  if (recentProjects) {
    const recentProjectOption: EntityWithRelationOption[] =
      recentProjects.flatMap((projectId) => {
        const currEntity = entities.find((e) => e.entity.id === projectId);

        return currEntity
          ? {
              id: `${projectId}_recent`,
              label: currEntity.entity.title,
              value: projectId,
              isRecent: true,
              children: null,
              properties: { upmostParentId: currEntity.upmostParentID },
              isPersonalSpace: currEntity.isPersonalSpace,
            }
          : [];
      });

    return [...recentProjectOption, ...options];
  } else {
    return [...options];
  }
};

export const getSelectOptionsFromEnumParam = (
  param: EntityParameter,
  checkedValues?: string[]
): ISelectOption[] =>
  param.settings.allowedValues
    ? param.settings.allowedValues.map((v) => ({
        label: v,
        value: v,
        checked: !!checkedValues && checkedValues.includes(v),
      }))
    : [];

export const exportEntityWrapper = async (id: number) => {
  notify.success(infoMessages.ENTITY_EXPORT_STARTED);
  await exportEntity({
    type: 'kmz',
    entityID: id,
  })
    .then((response) => {
      downloadObject(response.data, 'AstraExport.kmz');
    })
    .catch(() => notify.error(errorMessages.ENTITY_EXPORT_ERROR));
};

export const constructIdentifier = (id: number | string) =>
  String(id).padStart(5, '0');

const updateCurrentBoundary = (
  points: Position[],
  currentBoundary: CameraBounds
) =>
  points.reduce<CameraBounds>((pointAcc, pointCurr) => {
    const isWesternmostPoint = pointCurr[0] < pointAcc[0];
    const isSouthernmostPoint = pointCurr[1] < pointAcc[1];
    const isEasternmostPoint = pointCurr[0] > pointAcc[2];
    const isNorthernmostPoint = pointCurr[1] > pointAcc[3];
    return [
      isWesternmostPoint ? pointCurr[0] : pointAcc[0],
      isSouthernmostPoint ? pointCurr[1] : pointAcc[1],
      isEasternmostPoint ? pointCurr[0] : pointAcc[2],
      isNorthernmostPoint ? pointCurr[1] : pointAcc[3],
    ];
  }, currentBoundary);

export const getBoundsFromIDs = (
  entities: Entities,
  parametersIdTitleMap: ParametersIdTitleMap,
  objectIds: number[]
) =>
  objectIds.reduce(
    (acc, curr) => {
      const currEntity = entities[curr];
      const currGeometry: GeometryParameter | null = getEntityParameterValue(
        currEntity.entity,
        parametersIdTitleMap,
        mapEntityParams.GEOMETRY
      );
      return checkTypeAndUpdateBoundary(acc, currGeometry);
    },
    // [west, south, east, north]
    [180, 90, -180, -90] as CameraBounds
  );

export const checkTypeAndUpdateBoundary = (
  acc: CameraBounds,
  geometry: Point | Polygon | LineString | null
) => {
  if (!geometry) return acc;

  const currType = geometry.type;

  switch (currType) {
    case FeatureTypes.POINT:
      return updateCurrentBoundary([geometry.coordinates] as Position[], acc);
    case FeatureTypes.LINE:
      return updateCurrentBoundary(geometry.coordinates as Position[], acc);
    case FeatureTypes.POLYGON:
      return updateCurrentBoundary(
        geometry.coordinates.flat() as Position[],
        acc
      );
    default:
      return acc;
  }
};

export const getBoundsFromFeatureCollection = (
  featureCollection: GeoJSON.FeatureCollection
) =>
  featureCollection.features.reduce(
    (acc, curr) => {
      return checkTypeAndUpdateBoundary(
        acc,
        curr.geometry as Point | LineString | Polygon
      );
    },
    [180, 90, -180, -90] as CameraBounds
  );

export const excludeNewMediaFromEntity = (
  entity: Entity,
  paramIdMap: Record<string, string>
): Entity => {
  const mediaFiles: IMediaFile[] =
    entity.parameters[paramIdMap[mapEntityParams.MEDIA]].value;

  return {
    ...entity,
    parameters: {
      ...entity.parameters,
      [paramIdMap[mapEntityParams.MEDIA]]: {
        ...entity.parameters[paramIdMap[mapEntityParams.MEDIA]],
        value: mediaFiles.filter((v) => v.file === undefined),
      },
    },
  };
};

export const getTypeIcons = (template: PredefinedTemplate) => {
  const fetchedIcons =
    template.parameters.find(({ title }) => title === mapEntityParams.TYPE)
      ?.settings?.militaryClass ?? [];

  return extractIcons(fetchedIcons);
};

export const loadImagePromise = ({
  path,
  width,
  height,
}: {
  path: string;
  width: number;
  height: number;
}) =>
  new Promise<HTMLImageElement>((resolve) => {
    const image = new Image(width, height);
    image.addEventListener('load', () => resolve(image));
    image.src = path;
  });

export const changeViewBox = (svgString: string, desiredViewBox: string) =>
  svgString.replace(VIEW_BOX_REGEX, `viewBox="${desiredViewBox}"`);

export const loadSvgStringAsImagePromise = ({
  svgString,
  width,
  height,
}: {
  width: number;
  height: number;
  svgString: string;
}) =>
  loadImagePromise({
    path: 'data:image/svg+xml;charset=utf-8;base64,' + btoa(svgString),
    width,
    height,
  });
